import { useEffect, useMemo, useRef } from "react";
import { showInfoToast, showWarningToast, showErrorToast } from "../utils/toastUtils";

const parseThresholds = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return [70, 85, 95];
  const nums = raw
    .split(",")
    .map((v) => Number(String(v).trim()))
    .filter((n) => Number.isFinite(n) && n > 0 && n < 100);
  if (nums.length === 0) return [70, 85, 95];
  return [...new Set(nums)].sort((a, b) => a - b);
};

const clampPercent = (value) => Math.min(100, Math.max(0, Number(value) || 0));

const getStorageKey = (sessionId, cycleNumber = 1) =>
  sessionId
    ? `chat_session_usage_warned:${String(sessionId)}:cycle:${Number(cycleNumber) || 1}`
    : null;

const readShownThresholds = (sessionId, cycleNumber) => {
  const key = getStorageKey(sessionId, cycleNumber);
  if (!key) return new Set();
  try {
    const raw = sessionStorage.getItem(key);
    const arr = raw ? JSON.parse(raw) : null;
    if (!Array.isArray(arr)) return new Set();
    return new Set(
      arr
        .map((n) => Number(n))
        .filter((n) => Number.isFinite(n)),
    );
  } catch {
    return new Set();
  }
};

const writeShownThresholds = (sessionId, cycleNumber, set) => {
  const key = getStorageKey(sessionId, cycleNumber);
  if (!key) return;
  try {
    sessionStorage.setItem(key, JSON.stringify([...set.values()]));
  } catch {}
};

export const useSessionUsageWarnings = ({
  sessionId,
  tokenUsage,
  usageCycle,
  sessionBlocked,
  thresholdsCsv = process.env.REACT_APP_SESSION_USAGE_WARNING_THRESHOLDS,
}) => {
  const thresholds = useMemo(
    () => parseThresholds(thresholdsCsv),
    [thresholdsCsv],
  );

  const lastSessionIdRef = useRef(null);
  const lastCycleRef = useRef(null);
  const shownRef = useRef(new Set());
  const currentCycle = Number(usageCycle?.current_cycle || 1) || 1;

  useEffect(() => {
    if (
      lastSessionIdRef.current !== sessionId ||
      lastCycleRef.current !== currentCycle
    ) {
      lastSessionIdRef.current = sessionId || null;
      lastCycleRef.current = currentCycle;
      shownRef.current = readShownThresholds(sessionId, currentCycle);
    }
  }, [currentCycle, sessionId]);

  useEffect(() => {
    if (!sessionId) return;
    if (!usageCycle || typeof usageCycle !== "object") return;
    if (sessionBlocked) return;

    const total = Number(usageCycle.current_cycle_tokens ?? 0) || 0;
    const limit = Number(usageCycle.limit ?? 0) || 0;
    if (!(limit > 0)) return;

    const usedPercent = clampPercent(
      usageCycle.current_cycle_percent ?? (total / limit) * 100,
    );
    const remainingPercent = Math.max(0, Math.round(100 - usedPercent));
    const remainingTokens = Math.max(0, Math.round(limit - total));

    const shown = shownRef.current;
    for (const t of thresholds) {
      if (usedPercent < t) continue;
      if (shown.has(t)) continue;

      const msgBase =
        remainingTokens > 0
          ? `${remainingPercent}% of your session remaining (${remainingTokens.toLocaleString()} tokens left).`
          : `${remainingPercent}% of your session remaining.`;

      if (t >= 95) {
        showErrorToast(`Session limit almost reached — only ${msgBase}`);
      } else if (t >= 85) {
        showWarningToast(`Warning: Only ${msgBase}`);
      } else {
        showInfoToast(`You have ${msgBase}`);
      }

      shown.add(t);
      writeShownThresholds(sessionId, currentCycle, shown);
    }
  }, [currentCycle, sessionBlocked, sessionId, thresholds, usageCycle]);
};
